import {useMemo} from "react";
import {useHistory, useLocation, useParams, useRouteMatch} from 'react-router-dom';
import queryString from 'query-string';
import {QueryStringDefaultParseOptions} from "../../../core/constants/enums";

/**
 * Hook for React-router-dom Hooks
 *
 * The return object is :
 *
 * ```javascript
 * {
 *      parseUrl: (url: string, options?: ParseOptions) => ParsedUrl,
 *      query: {[p: string]: string[] | string | null},
 *      match: match<{}>,
 *      location: Location<LocationState>,
 *      history: History<LocationState>,
 *      params: {},
 *      stringify: (object: Record<string, any>, options?: StringifyOptions) => string,
 *      stringifyUrl: (object: UrlObject, options?: StringifyOptions) => string,
 *      pathname: string
 * }
 * ```
 * @param path {string | string[] | {path: string | string[]}}
 * @param {{arrayFormatSeparator: string; arrayFormat: string}} parseOptions
 */
const useRouter = (
    path = null,
    parseOptions = QueryStringDefaultParseOptions,
) => {
    const params = useParams();
    const history = useHistory();
    const location = useLocation();
    const match = useRouteMatch(path);
    const stringifyUrl = queryString.stringifyUrl;
    const stringify = queryString.stringify;
    const parseUrl = queryString.parseUrl;

    const __location = history?.location || location;

    return useMemo(() => {
        return {
            pathname: __location.pathname,
            query: {
                ...queryString.parse(__location.search, parseOptions),
            },
            params: {
                ...params,
            },
            match,
            location: __location,
            history,
            stringifyUrl,
            stringify,
            parseUrl,
        };
    }, [params, match, history, __location, __location?.pathname]);
}

export default useRouter;
